import { useEffect, useState } from "react";
import { Loader2, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { api, errMsg } from "@/lib/api";

const blank = (types, currency) => ({ type: Object.keys(types)[0], amount: "", date: "", currency: currency || "", notes: "" });

export const MovementForm = ({ open, onOpenChange, types, currency, onSubmit }) => {
  const [form, setForm] = useState(blank(types, currency));
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setForm(blank(types, currency)); setFile(null); } }, [open, types, currency]);
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let proof_file_id = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file, file.name);
        proof_file_id = (await api.post("/files", fd)).data.id;
      }
      await onSubmit({ type: form.type, amount: Number(form.amount), date: form.date || null, currency: form.currency || null, notes: form.notes.trim() || null, proof_file_id });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" data-testid="movement-form-sheet" className="max-h-[92vh] overflow-y-auto rounded-t-2xl pb-[max(1.5rem,env(safe-area-inset-bottom))]">
        <SheetHeader className="text-left">
          <SheetTitle className="font-heading">Nuevo movimiento</SheetTitle>
          <SheetDescription>Los movimientos no se editan ni se borran; quedan en el historial.</SheetDescription>
        </SheetHeader>
        <form onSubmit={submit} className="mt-4 space-y-3 max-w-md mx-auto">
          <div className="grid grid-cols-2 gap-2" role="radiogroup">
            {Object.entries(types).map(([k, v]) => (
              <button type="button" key={k} data-testid={`movement-type-${k}`} onClick={() => setForm((s) => ({ ...s, type: k }))}
                className={`py-2.5 rounded-lg border text-sm font-semibold transition-colors ${form.type === k ? "border-[#bc9538] c-accent bg-[#bc9538]/10" : "c-line c-text-2"}`}>{v}</button>
            ))}
          </div>
          <label className="block space-y-1"><span className="text-xs font-medium c-text-2">Monto *</span>
            <input data-testid="movement-amount-input" className="c-input font-mono" type="number" inputMode="decimal" step="any" min="0" required value={form.amount} onChange={set("amount")} />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block space-y-1"><span className="text-xs font-medium c-text-2">Fecha</span>
              <input data-testid="movement-date-input" className="c-input" type="date" value={form.date} onChange={set("date")} />
            </label>
            <label className="block space-y-1"><span className="text-xs font-medium c-text-2">Moneda</span>
              <select data-testid="movement-currency-select" className="c-input" value={form.currency} onChange={set("currency")}>
                <option value="">Sin definir</option><option>MXN</option><option>USD</option><option>EUR</option>
              </select>
            </label>
          </div>
          <label className="block space-y-1"><span className="text-xs font-medium c-text-2">Notas</span>
            <textarea data-testid="movement-notes-input" className="c-input" rows={2} value={form.notes} onChange={set("notes")} />
          </label>
          <label data-testid="movement-proof-label" className="flex items-center gap-2 c-input cursor-pointer c-text-2">
            <Paperclip className="w-4 h-4" /> <span className="truncate">{file ? file.name : "Comprobante (opcional): foto o PDF"}</span>
            <input data-testid="movement-proof-file-input" type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
          <button data-testid="movement-submit-btn" disabled={busy} className="c-btn-primary w-full py-3.5 flex items-center justify-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Registrar movimiento
          </button>
        </form>
      </SheetContent>
    </Sheet>
  );
};
